import { action, computed, IReactionDisposer, observable, when } from 'mobx';

import { RouterStore } from '@estee/elc-router';
import { ViewController } from '@estee/elc-service';
import { BrowserStorage, ViewControllerLoadedMethod } from '@estee/elc-universal-utils';

import { AccountSignInSignUpSearchParams, StorageKey } from '~constants/Constants';
import { SocialCallbackQueryParams } from '~constants/SocialConstants';
import { UserRepository } from '~repositories/UserRepository';
import { ACCOUNT_HOME_URL } from '~routes/routes';

export interface IBaseViewController {
    routerStore: RouterStore;
    userRepository: UserRepository;
}

export abstract class BaseViewController extends ViewController {
    protected routerStore: RouterStore;
    protected userRepository: UserRepository;

    protected isCheckoutFlow: boolean;
    protected redirectUrl: string;
    protected isGuest: boolean;

    private disposer: IReactionDisposer;

    @observable public hasError: boolean;

    constructor({ routerStore, userRepository }: IBaseViewController) {
        super();
        this.routerStore = routerStore;
        this.userRepository = userRepository;
    }

    abstract loaded: ViewControllerLoadedMethod;
    abstract getRedirectUrl(urlParams: URLSearchParams): string;

    @computed
    public get data() {
        return {
            hasError: this.hasError,
            isLoading: this.userRepository.isLoadingState
        };
    }

    @action
    protected setError(error: string) {
        this.hasError = true;
        this.setIsLoading(false);
    }

    @action
    protected setIsLoading(isLoading: boolean) {
        this.userRepository.setIsLoading(isLoading);
    }

    public init() {
        // queryParams doesn't work on Vulcan.
        const urlParams = new URLSearchParams(window.location.search);
        this.redirectUrl = this.getRedirectUrl(urlParams);
        this.isGuest = urlParams.get(AccountSignInSignUpSearchParams.IS_GUEST) === '1';

        // Require URLSearchParams here since the callback from social provider cannot include/set internal router
        const hasSocialState = urlParams.has(SocialCallbackQueryParams.STATE);
        // Should not clear redirect url in session from social call back
        if (!hasSocialState) {
            BrowserStorage.setItem(
                StorageKey.ACCOUNT_REDIRECT_URL,
                this.redirectUrl,
                'localStorage'
            );
            BrowserStorage.removeItem(StorageKey.SOCIAL_STATE, 'localStorage');
            this.userRepository.consentForm.setFormValuesBackup(undefined);
        }
    }

    public watchSessionStateChange = () => {
        this.disposer = when(
            () => !!this.userRepository.sessionResponse,
            () => {
                const { changeLocation } = this.routerStore;

                const { sessionResponse: { signedIn, isGuestCheckout } = {} } = this.userRepository;

                if (this.isCheckoutFlow && (signedIn || isGuestCheckout)) {
                    changeLocation(this.redirectUrl);
                } else if (signedIn) {
                    // no form will be displayed since configuration is not loaded
                    changeLocation(this.redirectUrl || ACCOUNT_HOME_URL);
                }
            }
        );
    };

    public dispose = () => {
        this.disposer?.();
    };
}
